import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import useAssetWorkflow from "../../hooks/useAssetWorkflow";
import useAssetDeleteWorkflow from "../../hooks/useAssetDeleteWorkflow";
import jurisdictionApi from "../../api/jurisdictionApi";
import {
  COMPANY_LIFECYCLE_OPTIONS,
  COMPANY_LIFECYCLE_STAGE_OPTIONS,
  VALID_STAGES_BY_LIFECYCLE,
} from "../../constants/companyFields";
import { formatJurisdictionLabel } from "../../utils/companiesFormatting";
import { CURRENCY_OPTIONS } from "./productionUtils";

const createDefaultAssetForm = (currency = "USD") => ({
  asset_name: "",
  jurisdiction: "",
  reporting_currency: currency,
  ownership_type: "Owner/Operator",
  ownership_percentage: "",
  lifecycle: "na",
  lifecycle_stage: "",
  start_year_of_operation: "",
  expected_first_production_year: "",
  closure_or_reclamation_year: "",
  has_historic_production: false,
  asset_page_url: "",
  latitude: "",
  longitude: "",
  geological_region: "",
  mining_district: "",
  land_size_acres: "",
  pct_land_explored: "",
});

export default function AssetDialogs({
  companyApi,
  companyId,
  canEditCompany,
  defaultReportingCurrency = "USD",
  onAssetSaved,
  onAssetDeleted,
  assetDialogRequest,
  onAssetDialogRequestHandled,
  assetDeleteRequest,
  onAssetDeleteRequestHandled,
  onDeleteFeedback,
}) {
  const excelTextFieldSx = {
    "& .MuiInputBase-input": {
      padding: "6px 8px",
      fontSize: "0.875rem", 
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.875rem",
      transform: "translate(8px, 8px) scale(1)",
    },

    "& .MuiInputLabel-shrink": {
      transform: "translate(8px, -6px) scale(0.85)",
    },
  };
  const [addAssetOpen, setAddAssetOpen] = useState(false);
  const [assetForm, setAssetForm] = useState(
    createDefaultAssetForm(defaultReportingCurrency)
  );
  const [assetDialogMode, setAssetDialogMode] = useState("create");
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [assetHasReports, setAssetHasReports] = useState(false);
  const [assetSaveState, setAssetSaveState] = useState({
    loading: false,
    error: null,
    success: null,
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [jurisdictionsLoading, setJurisdictionsLoading] = useState(false);
  const [jurisdictionsError, setJurisdictionsError] = useState(null);

  const stageOptions = useMemo(() => {
    const lifecycle = assetForm.lifecycle;
    const allowedStages = VALID_STAGES_BY_LIFECYCLE[lifecycle] || [];
    return COMPANY_LIFECYCLE_STAGE_OPTIONS.filter((opt) => allowedStages.includes(opt.value));
  }, [assetForm.lifecycle]);

  const sortedJurisdictions = useMemo(() => {
    return [...jurisdictions].sort((a, b) => {
      const aImputed = a.fraser_is_imputed ? 1 : 0;
      const bImputed = b.fraser_is_imputed ? 1 : 0;
      if (aImputed !== bImputed) return aImputed - bImputed;
      return (a.id || 0) - (b.id || 0);
    });
  }, [jurisdictions]);

  const selectedJurisdictionOption = useMemo(() => {
    const id = Number(assetForm.jurisdiction);
    if (!Number.isFinite(id)) return null;
    return sortedJurisdictions.find((item) => item.id === id) || null;
  }, [assetForm.jurisdiction, sortedJurisdictions]);

  useEffect(() => {
    let mounted = true;
    const loadJurisdictions = async () => {
      setJurisdictionsLoading(true);
      setJurisdictionsError(null);
      try {
        const data = await jurisdictionApi.getJurisdictionsSimple();
        const rows = Array.isArray(data) ? data : data?.results ?? [];
        if (mounted) setJurisdictions(rows);
      } catch (err) {
        console.error("Failed to load jurisdictions", err);
        if (mounted) {
          setJurisdictions([]);
          setJurisdictionsError("Unable to load jurisdictions.");
        }
      } finally {
        if (mounted) setJurisdictionsLoading(false);
      }
    };
    loadJurisdictions();
    return () => {
      mounted = false;
    };
  }, []);

  const defaultAssetForm = useMemo(
    () => createDefaultAssetForm(defaultReportingCurrency),
    [defaultReportingCurrency]
  );

  const {
    handleOpenAddAsset,
    handleOpenEditAsset,
    handleCloseAssetDialog,
    handleAssetFieldChange,
    handleSubmitAsset,
    handleUpdateAsset,
  } = useAssetWorkflow({
    companyApi,
    companyId,
    canEditCompany,
    assetForm,
    setAssetForm,
    setAssetSaveState,
    setAssetDialogMode,
    setEditingAssetId,
    setAddAssetOpen,
    onAssetSaved,
    defaultAssetForm,
    editingAssetId,
  });

  const { handleConfirmDelete } = useAssetDeleteWorkflow({
    companyApi,
    onAssetDeleted,
    deleteTarget,
    setDeleteTarget,
    setDeleteFeedback: onDeleteFeedback,
  });

  useEffect(() => {
    if (!assetDialogRequest) return;
    if (assetDialogRequest.mode === "edit") {
      const hasReports = Boolean(
        (assetDialogRequest.asset?.resource_reserves || []).some(
          (reserve) => (reserve?.production_reports || []).length
        )
      );
      setAssetHasReports(hasReports);
      handleOpenEditAsset(assetDialogRequest.asset);
    } else {
      setAssetHasReports(false);
      handleOpenAddAsset();
    }
    if (onAssetDialogRequestHandled) {
      onAssetDialogRequestHandled();
    }
  }, [
    assetDialogRequest,
    handleOpenAddAsset,
    handleOpenEditAsset,
    onAssetDialogRequestHandled,
  ]);

  useEffect(() => {
    if (!assetSaveState.success && !assetSaveState.error) return;
    const timer = setTimeout(() => {
      setAssetSaveState((prev) => ({ ...prev, success: null, error: null }));
    }, 4000);
    return () => clearTimeout(timer);
  }, [assetSaveState.error, assetSaveState.success]);

  useEffect(() => {
    if (!assetDeleteRequest) return;
    setDeleteTarget(assetDeleteRequest);
    if (onAssetDeleteRequestHandled) {
      onAssetDeleteRequestHandled();
    }
  }, [assetDeleteRequest, onAssetDeleteRequestHandled]);

  return (
    <>
      {assetSaveState.success && <Alert severity="success">{assetSaveState.success}</Alert>}
      {assetSaveState.error && <Alert severity="error">{assetSaveState.error}</Alert>}

      <Dialog open={addAssetOpen} onClose={handleCloseAssetDialog} fullWidth maxWidth="sm">
        <DialogTitle>{assetDialogMode === "edit" ? "Edit Asset" : "Add Asset"}</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: 1,
          }}
        >
          {assetSaveState.error && <Alert severity="error">{assetSaveState.error}</Alert>}
          <Typography variant="subtitle2" fontWeight={700}>
            Asset Info
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <TextField
              label="Asset name"
              required
              value={assetForm.asset_name}
              onChange={handleAssetFieldChange("asset_name")}
              fullWidth
              sx={excelTextFieldSx}
            />
            <TextField
              select
              label="Ownership type"
              value={assetForm.ownership_type}
              onChange={handleAssetFieldChange("ownership_type")}
              fullWidth
              sx={excelTextFieldSx}
            >
              <MenuItem value="Owner/Operator">Owner/Operator</MenuItem>
              <MenuItem value="Joint Venture/Partial Operator">Joint Venture/Partial Operator</MenuItem>
              <MenuItem value="Royalty/Streaming">Royalty/Streaming</MenuItem>
            </TextField>
            <TextField
              label="Ownership %"
              value={assetForm.ownership_percentage}
              onChange={handleAssetFieldChange("ownership_percentage")}
              type="number"
              fullWidth
              required
              sx={excelTextFieldSx}
            />
            <TextField
              select
              label="Reporting currency"
              value={assetForm.reporting_currency || defaultReportingCurrency}
              onChange={handleAssetFieldChange("reporting_currency")}
              fullWidth
              required
              disabled={assetDialogMode === "edit" && assetHasReports}
              sx={excelTextFieldSx}
              helperText={
                assetDialogMode === "edit" && assetHasReports
                  ? "Reporting currency is locked once reports exist."
                  : ""
              }
            >
              {CURRENCY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Asset page URL"
              value={assetForm.asset_page_url}
              onChange={handleAssetFieldChange("asset_page_url")}
              fullWidth
              sx={excelTextFieldSx}
            />
          </Box>
          <Autocomplete
            options={sortedJurisdictions}
            value={selectedJurisdictionOption}
            loading={jurisdictionsLoading}
            onChange={(_, value) =>
              handleAssetFieldChange("jurisdiction")({
                target: { value: value?.id || "" },
              })
            }
            getOptionLabel={(option) => formatJurisdictionLabel(option)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Jurisdiction"
                required
                placeholder="Search by country or region"
                error={Boolean(jurisdictionsError)}
                helperText={jurisdictionsError}
                sx={excelTextFieldSx}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {jurisdictionsLoading ? (
                        <CircularProgress color="inherit" size={16} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            fullWidth
            required
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <TextField
              label="Latitude"
              value={assetForm.latitude}
              onChange={handleAssetFieldChange("latitude")}
              type="number"
              fullWidth
              sx={excelTextFieldSx}
            />
            <TextField
              label="Longitude"
              value={assetForm.longitude}
              onChange={handleAssetFieldChange("longitude")}
              type="number"
              fullWidth
              sx={excelTextFieldSx}
            />
            <TextField
              label="Geological region"
              value={assetForm.geological_region}
              onChange={handleAssetFieldChange("geological_region")}
              fullWidth
              sx={excelTextFieldSx}
            />
            <TextField
              label="Mining district"
              value={assetForm.mining_district}
              onChange={handleAssetFieldChange("mining_district")}
              fullWidth
              sx={excelTextFieldSx}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(assetForm.has_historic_production)}
                  onChange={handleAssetFieldChange("has_historic_production")}
                />
              }
              label="Historical production"
            />
          </Box>
          <Divider />
          <Typography variant="subtitle2" fontWeight={700}>
            Lifecycle
          </Typography>
          <TextField
            select
            label="Lifecycle"
            value={assetForm.lifecycle}
            onChange={handleAssetFieldChange("lifecycle")}
            fullWidth
            required
            sx={excelTextFieldSx}
          >
            {COMPANY_LIFECYCLE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Lifecycle stage"
            value={assetForm.lifecycle_stage}
            onChange={handleAssetFieldChange("lifecycle_stage")}
            fullWidth
            required
            disabled={stageOptions.length === 0}
            sx={excelTextFieldSx}
          >
            <MenuItem value="">Select stage</MenuItem>
            {stageOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <Divider />
          <Typography variant="subtitle2" fontWeight={700}>
            Dates
          </Typography>
          {assetForm.lifecycle === "na" && (
            <Typography variant="caption" color="text.secondary">
              Select a lifecycle to display date fields.
            </Typography>
          )}
          {["exploration", "development"].includes(assetForm.lifecycle) && (
            <TextField
              label="Expected first production year"
              value={assetForm.expected_first_production_year}
              onChange={handleAssetFieldChange("expected_first_production_year")}
              type="number"
              fullWidth
              sx={excelTextFieldSx}
            />
          )}
          {assetForm.lifecycle === "production" && (
            <TextField
              label="Start year of operation"
              value={assetForm.start_year_of_operation}
              onChange={handleAssetFieldChange("start_year_of_operation")}
              type="number"
              fullWidth
              sx={excelTextFieldSx}
            />
          )}
          {assetForm.lifecycle === "closure" && (
            <TextField
              label="Closure/reclamation year"
              value={assetForm.closure_or_reclamation_year}
              onChange={handleAssetFieldChange("closure_or_reclamation_year")}
              type="number"
              fullWidth
              sx={excelTextFieldSx}
            />
          )}
          <Divider />
          <Typography variant="subtitle2" fontWeight={700}>
            Land
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <TextField
              label="Land size (acres)"
              value={assetForm.land_size_acres}
              onChange={handleAssetFieldChange("land_size_acres")}
              type="number"
              fullWidth
              sx={excelTextFieldSx}
            />
            <TextField
              label="% Land explored"
              value={assetForm.pct_land_explored}
              onChange={handleAssetFieldChange("pct_land_explored")}
              type="number"
              fullWidth
              sx={excelTextFieldSx}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssetDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={assetDialogMode === "edit" ? handleUpdateAsset : handleSubmitAsset}
            disabled={assetSaveState.loading}
          >
            {assetSaveState.loading
              ? "Saving..."
              : assetDialogMode === "edit"
              ? "Update asset"
              : "Submit asset"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete asset?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will request deletion of{" "}
            <strong>{deleteTarget?.asset_name || "this asset"}</strong>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete}>
            Confirm delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
